import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import {
  USERS_FILTER_ALL,
  USERS_PAGE_SIZE,
  USERS_SEARCH_DEBOUNCE_MS,
  USERS_SEARCH_MIN_LENGTH,
} from "../constants/users.constants";
import { usersService } from "../services/users.service";
import type { UserStatus } from "../types/create-user.types";
import type { UserListItem, UserStatusCounts } from "../types/users-api.types";

export type UsersStatusFilter = typeof USERS_FILTER_ALL | UserStatus;

export interface UsersRosterState {
  rows: UserListItem[];
  total: number;
  counts: UserStatusCounts | null;

  /** First load only — a refetch keeps the rows on screen. */
  isLoading: boolean;
  /** A load is in flight while rows are already painted. Dims the table; never blanks it. */
  isRefetching: boolean;
  /** The load failed. Distinct from "no rows matched", which is not an error. */
  loadFailed: boolean;
  /** Latched 401/403 for the session. */
  noAccess: boolean;

  page: number;
  pageSize: number;
  setPage: (page: number) => void;

  query: string;
  setQuery: (value: string) => void;
  /** Something was typed but is shorter than the API's floor — show the hint, send nothing. */
  isTermTooShort: boolean;

  status: UsersStatusFilter;
  setStatus: (value: UsersStatusFilter) => void;

  roleId: string;
  setRoleId: (value: string) => void;

  hasActiveFilters: boolean;
  clearFilters: () => void;
  /** Reload the listing AND the counts. */
  refresh: () => void;
  /** Reload only the counts — for a mutation that moves a figure without changing which rows match. */
  refreshCounts: () => void;
}

/**
 * One page of `GET /internal/users` with its roles embedded, plus the status counts that feed the tile strip.
 *
 * Deliberately self-contained rather than built on the access screen's `usePrincipalRoles`: importing that
 * would pull `settings/hooks/useAccessUsers` into the dependency graph, which imports this module's service
 * back — a cycle whose module-scoped `const` initialisation is exactly the shape that fails with a TDZ
 * `undefined`. The roles fan-out that hook exists for is unnecessary here anyway, because `include=roles`
 * already returns them with the page.
 */
export function useUsersRoster(): UsersRosterState {
  const [page, setPageState] = useState(1);
  const [query, setQueryState] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [status, setStatusState] = useState<UsersStatusFilter>(USERS_FILTER_ALL);
  const [roleId, setRoleIdState] = useState<string>(USERS_FILTER_ALL);

  const [rows, setRows] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  // `null` is UNKNOWN, never zero — a failed counts request must not read as "nobody is suspended".
  const [counts, setCounts] = useState<UserStatusCounts | null>(null);

  const [loadFailed, setLoadFailed] = useState(false);
  const [noAccess, setNoAccess] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [countsNonce, setCountsNonce] = useState(0);
  /**
   * Loading is DERIVED — the key of the request that has settled, compared against the key of the request the
   * current props describe. Setting a boolean synchronously inside the effect would be a cascading render,
   * which the repo's lint rules reject; this is the same shape `useOrganizationsCatalog` uses.
   */
  const [settledKey, setSettledKey] = useState<string | null>(null);

  /**
   * Monotonic guard. A response is only allowed to write state if no newer request has started since — an
   * unmount bumps it too, so a settling promise cannot set state on a dead component.
   */
  const loadSeq = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), USERS_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const trimmed = query.trim();
  const isTermTooShort = trimmed.length > 0 && trimmed.length < USERS_SEARCH_MIN_LENGTH;
  const serverSearch = debouncedQuery.length >= USERS_SEARCH_MIN_LENGTH ? debouncedQuery : undefined;

  // Page resets live in the SETTERS, not in an effect. An effect would first fire a request with the stale
  // `skip` — skip=200 against a three-row result — and paint an empty table under a total of 3.
  const setQuery = useCallback((next: string) => {
    setQueryState(next);
    setPageState(1);
  }, []);
  const setStatus = useCallback((next: UsersStatusFilter) => {
    setStatusState(next);
    setPageState(1);
  }, []);
  const setRoleId = useCallback((next: string) => {
    setRoleIdState(next);
    setPageState(1);
  }, []);
  const setPage = useCallback((next: number) => setPageState(Math.max(1, next)), []);

  const clearFilters = useCallback(() => {
    setQueryState("");
    setDebouncedQuery("");
    setStatusState(USERS_FILTER_ALL);
    setRoleIdState(USERS_FILTER_ALL);
    setPageState(1);
  }, []);

  const refresh = useCallback(() => {
    setReloadNonce((n) => n + 1);
    setCountsNonce((n) => n + 1);
  }, []);

  const refreshCounts = useCallback(() => setCountsNonce((n) => n + 1), []);

  const wireStatus = status === USERS_FILTER_ALL ? undefined : status;
  const wireRoleId = roleId === USERS_FILTER_ALL ? undefined : roleId;

  const requestKey = `${page}|${serverSearch ?? ""}|${wireStatus ?? ""}|${wireRoleId ?? ""}|${reloadNonce}`;
  const isFetching = settledKey !== requestKey;
  const hasLoadedOnce = settledKey !== null;

  // One effect drives the listing: every filter is both a query param and a dependency, so changing any of
  // them re-issues from the right offset with no separate "reset pagination" step.
  useEffect(() => {
    const seq = ++loadSeq.current;

    usersService
      .fetchList({
        skip: (page - 1) * USERS_PAGE_SIZE,
        limit: USERS_PAGE_SIZE,
        include: "roles",
        // Spread conditionally: `buildQueryString` keeps empty strings and the API runs
        // `forbidNonWhitelisted`, so a stray `?status=` is a 400 rather than an ignored param.
        ...(serverSearch ? { search: serverSearch } : {}),
        ...(wireStatus ? { status: wireStatus } : {}),
        ...(wireRoleId ? { role_id: wireRoleId } : {}),
      })
      .then((response) => {
        if (seq !== loadSeq.current) return;
        setRows(response.data);
        setTotal(response.total);
        setLoadFailed(false);
      })
      .catch((error: unknown) => {
        if (seq !== loadSeq.current) return;
        // A permission failure arrives as 401, not 403 — the guard throws UnauthorizedException for an
        // insufficient scope. Latching on both is why this must not be read as "the session died".
        if (HttpApiService.isAccessDeniedError(error)) {
          setNoAccess(true);
          return;
        }
        // Recorded, not just toasted: an empty table with no error reads as "nobody matches", which is a
        // very different and far more reassuring claim than "we could not load this".
        setLoadFailed(true);
      })
      .finally(() => {
        if (seq !== loadSeq.current) return;
        setSettledKey(requestKey);
      });

    return () => {
      loadSeq.current += 1;
    };
  }, [requestKey, page, serverSearch, wireStatus, wireRoleId, reloadNonce]);

  // Counts take the same filters MINUS `status` — each tile reports the size of its own bucket, not of the
  // current view — and minus pagination. The endpoint ignores those params anyway; not sending them keeps
  // the intent legible.
  useEffect(() => {
    let cancelled = false;
    usersService
      .fetchCounts({
        ...(serverSearch ? { search: serverSearch } : {}),
        ...(wireRoleId ? { role_id: wireRoleId } : {}),
      })
      .then((data) => {
        if (!cancelled) setCounts(data);
      })
      .catch(() => {
        if (!cancelled) setCounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, [serverSearch, wireRoleId, countsNonce]);

  const hasActiveFilters = serverSearch !== undefined || wireStatus !== undefined || wireRoleId !== undefined;

  return useMemo(
    () => ({
      rows,
      total,
      counts,
      isLoading: isFetching && !hasLoadedOnce,
      isRefetching: isFetching && hasLoadedOnce,
      loadFailed,
      noAccess,
      page,
      pageSize: USERS_PAGE_SIZE,
      setPage,
      query,
      setQuery,
      isTermTooShort,
      status,
      setStatus,
      roleId,
      setRoleId,
      hasActiveFilters,
      clearFilters,
      refresh,
      refreshCounts,
    }),
    [
      rows,
      total,
      counts,
      isFetching,
      hasLoadedOnce,
      loadFailed,
      noAccess,
      page,
      setPage,
      query,
      setQuery,
      isTermTooShort,
      status,
      setStatus,
      roleId,
      setRoleId,
      hasActiveFilters,
      clearFilters,
      refresh,
      refreshCounts,
    ],
  );
}
